// Resumen de control del reporte (pantalla, PDF y CSV): lo que una
// aseguradora, una agencia o el ICT necesitan ver para comprobar que hubo
// control periódico. Tres bloques: inspección diaria (cobertura por día),
// árboles (evaluaciones) y cables (eventos e inspección anual). Lógica pura,
// sin red ni React: todo se calcula en memoria sobre lo ya cargado.
import { normalizarTexto } from './auditoria';
import { compararCodigo, esRiesgoAlto, RIESGO_ORDEN } from './arboles';

// ── Inspección diaria ──

export const esDeclarada = (r) => r?.origen === 'declarada';

// Las marcas del cierre técnico (miércoles de temporada baja) no se quitan:
// son una decisión de operación, no un error de registro.
export const esCierreTecnico = (marca) => /cierre tecnico/.test(normalizarTexto(marca?.motivo));

// Estado de un día para el calendario. Un día con inspección muestra su peor
// resultado (falla > alerta); si todas sus inspecciones son declaradas, se
// distingue como "declarada".
export function estadoCelda(d) {
  if (!d) return 'fuera';
  if (d.estado !== 'inspeccionado') return d.estado;
  if (d.resultado === 'falla') return 'falla';
  if (d.resultado === 'alerta') return 'alerta';
  return d.inspecciones.length && d.inspecciones.every(esDeclarada) ? 'declarada' : 'inspeccionado';
}

export const ESTADO_CELDA_LABEL = {
  inspeccionado: 'Con inspección',
  declarada: 'Declarada',
  alerta: 'Con alerta',
  falla: 'Con falla',
  sin_operacion: 'No se operó',
  sin_registro: 'Sin inspección',
  pendiente: 'Hoy, pendiente',
};

export function resumenDiario(dias) {
  const r = {
    dias: 0, conInspeccion: 0, diasDeclarados: 0, sinOperacion: 0, sinRegistro: 0, pendienteHoy: 0,
    inspecciones: 0, declaradas: 0, diasConAlerta: 0, diasConFalla: 0, conAlerta: 0, conFalla: 0,
  };
  for (const d of dias || []) {
    r.dias++;
    const c = estadoCelda(d);
    if (d.estado === 'inspeccionado') r.conInspeccion++;
    else if (d.estado === 'sin_operacion') r.sinOperacion++;
    else if (d.estado === 'pendiente') r.pendienteHoy++;
    else r.sinRegistro++;
    if (c === 'declarada') r.diasDeclarados++;
    if (c === 'alerta') r.diasConAlerta++;
    if (c === 'falla') r.diasConFalla++;
    r.inspecciones += d.inspecciones.length;
    r.declaradas += d.inspecciones.filter(esDeclarada).length;
    r.conAlerta += d.inspecciones.filter((x) => x.estado_general === 'alerta').length;
    r.conFalla += d.inspecciones.filter((x) => x.estado_general === 'falla').length;
  }
  const operables = r.dias - r.sinOperacion - r.pendienteHoy;
  r.cumplimiento = operables > 0 ? Math.round((r.conInspeccion / operables) * 1000) / 10 : null;
  return r;
}

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'setiembre', 'octubre',
  'noviembre', 'diciembre'];
export const nombreMes = (clave) => {
  const [y, m] = String(clave).split('-').map(Number);
  return `${MESES[m - 1] || ''} ${y}`;
};

const diaSemanaLunes = (iso) => (new Date(`${iso}T12:00:00Z`).getUTCDay() + 6) % 7; // 0 = lunes
const diasDelMes = (y, m) => new Date(Date.UTC(y, m, 0, 12)).getUTCDate();

// Meses del período con sus celdas (semana empieza en lunes). Cada celda:
// { fecha, dia, estado } con estado 'fuera' para los días del mes que no
// están en el período (o que todavía no llegan); null = hueco antes del 1.
export function mesesCalendario(dias, { desde, hasta } = {}) {
  const porFecha = new Map((dias || []).map((d) => [d.fecha, d]));
  const ini = desde || dias?.[0]?.fecha;
  const fin = hasta || dias?.[dias.length - 1]?.fecha;
  if (!ini || !fin || ini > fin) return [];
  const out = [];
  let [y, m] = ini.split('-').map(Number);
  const [yf, mf] = fin.split('-').map(Number);
  while (y < yf || (y === yf && m <= mf)) {
    const clave = `${y}-${String(m).padStart(2, '0')}`;
    const celdas = Array(diaSemanaLunes(`${clave}-01`)).fill(null);
    for (let n = 1; n <= diasDelMes(y, m); n++) {
      const fecha = `${clave}-${String(n).padStart(2, '0')}`;
      const d = porFecha.get(fecha);
      celdas.push({ fecha, dia: n, estado: d ? estadoCelda(d) : 'fuera', info: d || null });
    }
    out.push({ clave, titulo: nombreMes(clave), celdas });
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return out;
}

// Alertas y fallas agrupadas por ítem del checklist (más frecuentes primero).
export function hallazgosPorItem(hallazgos) {
  const m = new Map();
  for (const h of hallazgos || []) {
    if (!m.has(h.itemKey)) m.set(h.itemKey, { itemKey: h.itemKey, item: h.item, orden: h.orden, alertas: 0, fallas: 0, ultima: '' });
    const x = m.get(h.itemKey);
    if (h.estado === 'falla') x.fallas++; else x.alertas++;
    if ((h.fecha || '') > x.ultima) x.ultima = h.fecha;
  }
  return [...m.values()].map((x) => ({ ...x, total: x.alertas + x.fallas }))
    .sort((a, b) => b.total - a.total || b.fallas - a.fallas || (a.orden ?? 99) - (b.orden ?? 99));
}

// ── Árboles ──

const enPeriodo = (fecha, desde, hasta) => !!fecha && (!desde || fecha >= desde) && (!hasta || fecha <= hasta);
const porFechaDesc = (a, b) => (b.fecha || '').localeCompare(a.fecha || '')
  || (b.created_at || '').localeCompare(a.created_at || '') || String(b.id).localeCompare(String(a.id));

// arboles: salida de arbolesParaReporte (ya filtrados por instalación) o null.
// evaluaciones: TODAS las de cables_arbol_evaluaciones (o null si no se pudo).
export function arbolesControl({ arboles, evaluaciones, desde, hasta }) {
  if (!Array.isArray(arboles)) return null;
  const porArbol = new Map();
  for (const e of evaluaciones || []) {
    if (!e || e.anulada || !enPeriodo(e.fecha, desde, hasta)) continue;
    if (!porArbol.has(e.arbol_id)) porArbol.set(e.arbol_id, []);
    porArbol.get(e.arbol_id).push(e);
  }
  const filas = [...arboles].sort(compararCodigo).map((a) => {
    const evs = (porArbol.get(a.id) || []).sort(porFechaDesc);
    return { ...a, evaluacionesPeriodo: evs, nPeriodo: evs.length };
  });
  const evaluacionesPeriodo = filas.flatMap((a) => a.evaluacionesPeriodo.map((e) => ({ ...e, arbol: a })))
    .sort(porFechaDesc);
  return {
    filas,
    evaluacionesPeriodo,
    evaluacionesDisponibles: Array.isArray(evaluaciones),
    resumen: {
      arboles: filas.length,
      evaluadosPeriodo: filas.filter((a) => a.nPeriodo > 0).length,
      evaluacionesPeriodo: evaluacionesPeriodo.length,
      vencidas: filas.filter((a) => a.vencida).length,
      riesgoMedioOMas: filas.filter((a) => (RIESGO_ORDEN[a.riesgo] || 0) >= RIESGO_ORDEN.medio).length,
      riesgoAlto: filas.filter((a) => esRiesgoAlto(a.riesgo)).length,
      sinEvaluacion: filas.filter((a) => a.sin_evaluacion).length,
    },
  };
}

// ── Cables ──

// lineas: salida de lineasParaReporte (ya filtrados por instalación) o null.
// eventos: TODOS los de cables_linea_eventos (o null si no se pudo).
export function cablesControl({ lineas, eventos, desde, hasta }) {
  if (!Array.isArray(lineas)) return null;
  const todos = new Map();
  for (const e of eventos || []) {
    if (!e || e.anulada) continue;
    if (!todos.has(e.linea_id)) todos.set(e.linea_id, []);
    todos.get(e.linea_id).push(e);
  }
  const filas = [...lineas].sort(compararCodigo).map((l) => {
    const evs = (todos.get(l.id) || []).sort(porFechaDesc);
    const periodo = evs.filter((e) => enPeriodo(e.fecha, desde, hasta));
    // Última inspección anual (de un tercero) registrada hasta el fin del período.
    const anual = evs.find((e) => e.tipo === 'inspeccion_anual' && (!hasta || e.fecha <= hasta)) || null;
    return {
      ...l,
      eventosPeriodo: periodo,
      nPeriodo: periodo.length,
      anual,
      anualEnPeriodo: periodo.some((e) => e.tipo === 'inspeccion_anual'),
      sinFechaInstalacion: !l.fecha_instalacion,
    };
  });
  const eventosPeriodo = filas.flatMap((l) => l.eventosPeriodo.map((e) => ({ ...e, linea: l }))).sort(porFechaDesc);
  return {
    filas,
    eventosPeriodo,
    eventosDisponibles: Array.isArray(eventos),
    resumen: {
      cables: filas.length,
      conEventosPeriodo: filas.filter((l) => l.nPeriodo > 0).length,
      eventosPeriodo: eventosPeriodo.length,
      anualEnPeriodo: filas.filter((l) => l.anualEnPeriodo).length,
      sinAnualEnPeriodo: filas.filter((l) => !l.anualEnPeriodo).length,
      vidaVencida: filas.filter((l) => l.cambio_vencido).length,
      sinFechaInstalacion: filas.filter((l) => l.sinFechaInstalacion).length,
    },
  };
}

// Inspecciones del período en orden cronológico (anexo del PDF y CSV).
export const inspeccionesCronologicas = (rows) => [...(rows || [])].sort((a, b) =>
  (a.fecha || '').localeCompare(b.fecha || '') || (a.created_at || '').localeCompare(b.created_at || '')
  || String(a.id).localeCompare(String(b.id)));
